import { useState, useEffect, ChangeEvent } from 'react'
import { supabase } from '../../lib/supabase'
import CustomConfirmModal from '../common/CustomConfirmModal'
import styles from './BatchAddListModal.module.css'

interface ReadingList {
    id: string
    name: string
    created_at: string
}

interface NotificationState {
    isOpen: boolean
    title: string
    message: string
    type: 'success' | 'danger' | 'warning' | 'info'
}

interface BatchAddListModalProps {
    selectedCount: number
    onClose: () => void
    onConfirm: (listId: string, listName: string) => void
}

export default function BatchAddListModal({ selectedCount, onClose, onConfirm }: Readonly<BatchAddListModalProps>) {
    const [lists, setLists] = useState<ReadingList[]>([])
    const [newListName, setNewListName] = useState('')
    const [loading, setLoading] = useState(true)
    const [creating, setCreating] = useState(false)
    const [selectedListId, setSelectedListId] = useState<string | null>(null)

    const [notification, setNotification] = useState<NotificationState>({
        isOpen: false,
        title: '',
        message: '',
        type: 'info',
    })

    const showNotify = (title: string, message: string, type: NotificationState['type'] = 'info') => {
        setNotification({ isOpen: true, title, message, type })
    }

    const fetchLists = async () => {
        try {
            const {
                data: { user },
            } = await supabase.auth.getUser()
            if (!user) return

            const { data } = await supabase
                .from('reading_lists')
                .select('id, name, created_at')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false })

            if (data) setLists(data)
        } catch (error) {
            console.error('Error fetching lists:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchLists()
    }, [])

    const handleCreateList = async () => {
        if (!newListName.trim()) return
        setCreating(true)
        try {
            const {
                data: { user },
            } = await supabase.auth.getUser()
            if (!user) return

            const { data, error } = await supabase
                .from('reading_lists')
                .insert([{ name: newListName.trim(), user_id: user.id }])
                .select()
                .single()

            if (error) throw error

            setLists([data, ...lists])
            setSelectedListId(data.id)
            setNewListName('')
        } catch (error) {
            console.error('Error creating list:', error)
            showNotify('Error', 'Error al crear la lista', 'danger')
        } finally {
            setCreating(false)
        }
    }

    const handleConfirm = () => {
        if (!selectedListId) return
        const listName = lists.find((l) => l.id === selectedListId)?.name
        if (listName) {
            onConfirm(selectedListId, listName)
        }
    }

    return (
        <div className={styles.batchAddOverlay}>
            <div className={`${styles.batchAddModal} animate-scale-in`}>
                <div className={styles.batchAddHeader}>
                    <h3>Añadir {selectedCount} libros a lista</h3>
                    <button className={styles.batchCloseBtn} onClick={onClose}>
                        ✕
                    </button>
                </div>

                <div className={styles.batchAddBody}>
                    <p className={styles.batchInstruction}>Elige una lista existente o crea una nueva:</p>

                    {/* List Selection */}
                    <div className={styles.listSelectionGrid}>
                        {loading ? (
                            <div className="spinner small"></div>
                        ) : (
                            lists.map((list) => (
                                <label key={list.id} className={`${styles.listOption} ${selectedListId === list.id ? styles.selected : ''}`} data-testid="list-option" data-selected={selectedListId === list.id}>
                                    <input
                                        type="radio"
                                        name="list-selection"
                                        value={list.id}
                                        checked={selectedListId === list.id}
                                        onChange={() => setSelectedListId(list.id)}
                                        className="sr-only"
                                    />
                                    <span className={styles.listIcon}>📂</span>
                                    <span className={styles.listName}>{list.name}</span>
                                </label>
                            ))
                        )}
                        {lists.length === 0 && !loading && <p className="no-lists-msg">No tienes listas creadas aún.</p>}
                    </div>

                    {/* Create New List */}
                    <div className={styles.createNewDivider}>
                        <span>o crea una nueva</span>
                    </div>

                    <div className={styles.createListRow}>
                        <input
                            type="text"
                            placeholder="Nombre de nueva lista..."
                            value={newListName}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setNewListName(e.target.value)}
                            className={styles.newListInputModal}
                        />
                        <button className={styles.createListBtnModal} onClick={handleCreateList} disabled={!newListName.trim() || creating}>
                            {creating ? <span className="spinner small"></span> : 'Crear'}
                        </button>
                    </div>
                </div>

                <div className={styles.batchAddFooter}>
                    <button className={styles.btnSecondary} onClick={onClose}>
                        Cancelar
                    </button>
                    <button className={styles.btnPrimary} onClick={handleConfirm} disabled={!selectedListId}>
                        Añadir a lista
                    </button>
                </div>
            </div>

            <CustomConfirmModal
                isOpen={notification.isOpen}
                type={notification.type}
                title={notification.title}
                message={notification.message}
                showCancel={false}
                confirmText="Entendido"
                onConfirm={() => setNotification({ ...notification, isOpen: false })}
            />
        </div>
    )
}
